import type { Component } from 'solid-js'
import { createResource, createSignal, Show, createMemo } from 'solid-js'
import {
  getDateRangesForSimplePeriod,
  getDateRangesForCustomPeriod,
  formatDateToApiString,
  type SimplePeriodType,
  type CompareMode
} from '../../shared/helpers/date.functions'
import { useCorisEngagement } from '../../shared/providers/coris/coris-engagement.provider'
import { useUser } from '../../shared/providers/user-provider'
import { usePeriods } from '../../shared/providers/period.provider'
import PeriodSelector from '../shared/components/PeriodSelector'
import SummaryCard from '../shared/components/SummaryCard'
import EventsTable from '../shared/components/EventsTable'
import EventsChart from '../shared/components/EventsChart'
import EventsViewToggle from '../shared/components/EventsViewToggle'
import EmptyState from '../shared/components/EmptyState'
import { sticky } from '../../shared/directives/sticky'
import { SkeletonSummaryCard, SkeletonChart } from '../shared/components/Skeleton'
import type { EventsViewType, EventsTimeSeriesData } from '../../features/engagement/domain/models/events'

// Prevent tree-shaking of directive
sticky

const PAGE_SIZE = 15

const Events: Component = () => {
  const { engagementPeriod, setEngagementPeriod } = usePeriods()
  const [selectedView, setSelectedView] = createSignal<EventsViewType>('cities')
  const [currentPage, setCurrentPage] = createSignal(1)
  const [sortBy, setSortBy] = createSignal<string>('buy_now_clicks')
  const [sortOrder, setSortOrder] = createSignal<'asc' | 'desc'>('desc')
  const [isStuck, setIsStuck] = createSignal(false)

  const { getEvents } = useCorisEngagement()
  const { organizationSlug } = useUser()

  const [eventsData] = createResource(
    () => {
      const organization = organizationSlug()
      if (!organization) return null
      return {
        ...engagementPeriod(),
        viewType: selectedView(),
        page: currentPage(),
        sortBy: sortBy(),
        sortOrder: sortOrder(),
        organization
      }
    },
    async (params) => {
      if (!params) return null

      // Get date ranges based on period type
      const dateRanges =
        params.selectedPeriod === 'custom' && params.customDateRange
          ? getDateRangesForCustomPeriod(params.customDateRange.from, params.customDateRange.to)
          : getDateRangesForSimplePeriod(params.selectedPeriod)

      let fromPrevious = dateRanges.fromPrevious
      let toPrevious = dateRanges.toPrevious

      if (params.compareMode === 'custom_range' && params.compareCustomRange) {
        fromPrevious = formatDateToApiString(params.compareCustomRange.from)
        toPrevious = formatDateToApiString(params.compareCustomRange.to)
      }

      return await getEvents.execute({
        fromDate: dateRanges.fromDate,
        toDate: dateRanges.toDate,
        fromPrevious,
        toPrevious,
        sortBy: params.sortBy,
        sortOrder: params.sortOrder,
        page: params.page,
        pageSize: PAGE_SIZE,
        viewType: params.viewType,
        organization: params.organization
      })
    }
  )

  const isEmpty = createMemo(() => {
    const data = eventsData()
    return (
      data &&
      data.items.length === 0 &&
      data.summary.buyNowClicks.current === 0 &&
      data.summary.presaleCodes.current === 0 &&
      data.summary.meetAndGreet.current === 0 &&
      data.summary.vip.current === 0 &&
      data.summary.rsvp.current === 0
    )
  })

  const handlePeriodChange = (period: SimplePeriodType) => {
    setEngagementPeriod({ selectedPeriod: period })
    setCurrentPage(1)
  }

  const handleCustomRangeChange = (from: Date, to: Date) => {
    setEngagementPeriod({ customDateRange: { from, to } })
    setCurrentPage(1)
  }

  const handleCompareModeChange = (mode: CompareMode) => {
    setEngagementPeriod({ compareMode: mode })
  }

  const handleCompareCustomRangeChange = (from: Date, to: Date) => {
    setEngagementPeriod({ compareCustomRange: { from, to } })
  }

  const handleViewChange = (view: EventsViewType) => {
    setSelectedView(view)
    setCurrentPage(1)
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handleSort = (column: string, direction: 'asc' | 'desc') => {
    setSortBy(column)
    setSortOrder(direction)
    setCurrentPage(1)
  }

  const chartData = createMemo(() => {
    const data = eventsData()
    if (!data) return []

    return data.items.map((item) => {
      let label = ''
      if (selectedView() === 'cities') {
        label = item.city
      } else {
        // For events view, use the event name if available, otherwise fallback to city
        label = item.event || item.city
      }

      return {
        label,
        buyNowClicks: item.buyNowClicks.current,
        presaleCodes: item.presaleCodes.current,
        meetAndGreet: item.meetAndGreet.current,
        vip: item.vip.current,
        rsvp: item.rsvp.current
      }
    }) as EventsTimeSeriesData[]
  })

  return (
    <div class="flex flex-col gap-4 px-6">
      <div
        use:sticky={setIsStuck}
        class="sticky top-0 z-10 py-6 transition-colors duration-200 -mx-6 px-6"
        classList={{
          'bg-white border-b border-gray-200': isStuck(),
          'bg-transparent border-b border-transparent': !isStuck()
        }}
      >
        <div class="mx-auto flex w-full max-w-[1280px] flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <PeriodSelector
            selectedPeriod={engagementPeriod().selectedPeriod}
            onPeriodChange={handlePeriodChange}
            customDateRange={engagementPeriod().customDateRange}
            onCustomRangeChange={handleCustomRangeChange}
            compareMode={engagementPeriod().compareMode}
            onCompareModeChange={handleCompareModeChange}
            compareCustomRange={engagementPeriod().compareCustomRange}
            onCompareCustomRangeChange={handleCompareCustomRangeChange}
          />
          <EventsViewToggle selectedView={selectedView()} onViewChange={handleViewChange} />
        </div>
      </div>

      <div class="mx-auto flex w-full max-w-[1280px] flex-col gap-8">
        <Show when={!eventsData.loading && eventsData() && isEmpty()}>
          <EmptyState />
        </Show>

        <Show when={!isEmpty()}>
          <Show
            when={eventsData()?.summary}
            fallback={
              <div class="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
                <SkeletonSummaryCard />
                <SkeletonSummaryCard />
                <SkeletonSummaryCard />
                <SkeletonSummaryCard />
                <SkeletonSummaryCard />
              </div>
            }
          >
            <div class="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
              <SummaryCard
                title="ls_events_buy_now_clicks"
                value={eventsData()!.summary.buyNowClicks.current}
                change={eventsData()!.summary.buyNowClicks.diff}
              />
              <SummaryCard
                title="ls_events_presale_codes"
                value={eventsData()!.summary.presaleCodes.current}
                change={eventsData()!.summary.presaleCodes.diff}
              />
              <SummaryCard
                title="ls_events_meet_and_greet"
                value={eventsData()!.summary.meetAndGreet.current}
                change={eventsData()!.summary.meetAndGreet.diff}
              />
              <SummaryCard
                title="ls_events_vip"
                value={eventsData()!.summary.vip.current}
                change={eventsData()!.summary.vip.diff}
              />
              <SummaryCard
                title="ls_events_rsvp"
                value={eventsData()!.summary.rsvp.current}
                change={eventsData()!.summary.rsvp.diff}
              />
            </div>
          </Show>

          <Show when={chartData().length > 0} fallback={<SkeletonChart legendItems={5} />}>
            <div class="animate-fade-in">
              <EventsChart data={chartData()} />
            </div>
          </Show>

          <EventsTable
            items={eventsData()?.items ?? []}
            totalItems={eventsData()?.total ?? 0}
            viewType={selectedView()}
            page={currentPage}
            pageSize={PAGE_SIZE}
            onPageChange={handlePageChange}
            onSort={handleSort}
            sortColumn={sortBy}
            sortDirection={sortOrder}
            isLoading={eventsData.state === 'unresolved' || eventsData.loading}
          />
        </Show>
      </div>
    </div>
  )
}

export default Events
